import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { FORGE_CHAIN_OPTIONS as CHAIN_OPTIONS } from '../../config/chains';
import { chainFilterParam, stringParam, useUrlFilterState } from '../../hooks/useUrlFilterState';
import { FORGE_REMOTE_DC_TOOL_PATH } from '../../paths';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  RemoteDataCollectionChainSnapshot,
  RemoteDataCollectionDashboardResponse,
  RemoteDataCollectionDefaults,
  RemoteDataCollectionRefreshRequest,
  RemoteDataCollectionRow,
  RemoteDataCollectionRunResponse,
  RemoteDataCollectionUnstructuredPogFilter,
} from '../../types';
import {
  isForgeRefreshRunFresh,
  isForgeRefreshRunTimedOut,
  isTerminalForgeRefreshRunPollError,
  refreshRunCreatedAtMs,
} from '../../utils/refreshRuns';
import {
  REMOTE_DC_DEFAULT_CARTON_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_DELTA_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_DELTA_THRESHOLD_PCT,
  REMOTE_DC_DEFAULT_DETECTED_POLYGON_NORMALIZATION_PCT,
  REMOTE_DC_DEFAULT_INCLUDE_DELTA_PRIORITY,
  REMOTE_DC_DEFAULT_INCLUDE_SIMILARITY_PRIORITY,
  REMOTE_DC_DEFAULT_LOOKBACK_HOURS,
  REMOTE_DC_DEFAULT_LOW_EMBEDDING_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_LOW_EMBEDDING_THRESHOLD,
  REMOTE_DC_DEFAULT_PRODUCT_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_SAMPLE_SIZE,
  REMOTE_DC_DEFAULT_SEARCH_SIZE,
  REMOTE_DC_DEFAULT_SIMILARITY_PRIORITY_WEIGHT,
  REMOTE_DC_DEFAULT_SIMILARITY_THRESHOLD_PCT,
  REMOTE_DC_DEFAULT_SKIP_INFERENCES_WITHOUT_POGS,
  REMOTE_DC_DEFAULT_SSM_CONFIDENCE_THRESHOLD_PCT,
  REMOTE_DC_DEFAULT_UNSTRUCTURED_POG_FILTER,
  REMOTE_DC_MAX_SAMPLE_SIZE,
  REMOTE_DC_MAX_SEARCH_SIZE,
} from '../../utils/remoteDataCollectionDefaults';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../../utils/safeStorage';

const ROW_LIMIT = 100;
const DASHBOARD_MERGE_LINKED_CANDIDATES = true;
const ACTIVE_REMOTE_DC_RUNS_STORAGE_KEY = 'forge-remote-dc-active-runs';
const REFRESH_ALL_CONFIRMATION_MESSAGE =
  "Refresh all chains?\n\nThis starts refreshes for every supported chain and can take a while. If you only need one chain, use that chain's Refresh button instead.";
const LOOKBACK_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: '1', label: 'Last 1h' },
  { value: '2', label: 'Last 2h' },
  { value: '4', label: 'Last 4h' },
  { value: '8', label: 'Last 8h' },
  { value: '24', label: 'Last 1 day' },
];
const UNSTRUCTURED_POG_FILTER_OPTIONS: Array<{
  value: RemoteDataCollectionUnstructuredPogFilter;
  label: string;
}> = [
  { value: 'skip_if_all', label: 'Skip if all' },
  { value: 'skip_if_any', label: 'Skip if any' },
  { value: 'keep_all', label: 'Keep all' },
];

function clampRangeValue(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function rangeStepPrecision(step: number): number {
  return String(step).split('.')[1]?.length ?? 0;
}

function snapRangeValue(value: number, min: number, max: number, step: number): number {
  const clampedValue = clampRangeValue(value, min, max);
  const steppedValue = min + Math.round((clampedValue - min) / step) * step;
  return Number(clampRangeValue(steppedValue, min, max).toFixed(rangeStepPrecision(step)));
}

function isRangeValueStepAligned(value: number, min: number, max: number, step: number): boolean {
  const clampedValue = clampRangeValue(value, min, max);
  return Math.abs(snapRangeValue(value, min, max, step) - clampedValue) < 1e-9;
}

function parseRangeDraftValue(value: string): number | null {
  const trimmedValue = value.trim();
  if (
    !trimmedValue ||
    trimmedValue === '-' ||
    trimmedValue === '+' ||
    trimmedValue === '.' ||
    trimmedValue === '-.' ||
    trimmedValue === '+.' ||
    trimmedValue.endsWith('.')
  ) {
    return null;
  }
  const nextValue = Number(trimmedValue);
  return Number.isFinite(nextValue) ? nextValue : null;
}

type ActiveRemoteDataCollectionRun = {
  chainId: number;
  runId: string;
  requestedAtMs: number;
};

function isActiveRemoteDataCollectionRun(value: unknown): value is ActiveRemoteDataCollectionRun {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ActiveRemoteDataCollectionRun>;
  return (
    typeof candidate.chainId === 'number' &&
    typeof candidate.runId === 'string' &&
    typeof candidate.requestedAtMs === 'number'
  );
}

function readActiveRemoteDataCollectionRuns(): ActiveRemoteDataCollectionRun[] {
  const raw = safeLocalStorageGetItem(ACTIVE_REMOTE_DC_RUNS_STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is ActiveRemoteDataCollectionRun =>
        isActiveRemoteDataCollectionRun(entry) && isForgeRefreshRunFresh(entry.requestedAtMs)
    );
  } catch {
    return [];
  }
}

function writeActiveRemoteDataCollectionRuns(runs: ActiveRemoteDataCollectionRun[]): void {
  safeLocalStorageSetItem(ACTIVE_REMOTE_DC_RUNS_STORAGE_KEY, JSON.stringify(runs));
}

function sameActiveRemoteDataCollectionRuns(
  left: ActiveRemoteDataCollectionRun[],
  right: ActiveRemoteDataCollectionRun[]
): boolean {
  if (left.length !== right.length) return false;
  return left.every((entry, index) => {
    const other = right[index];
    return (
      other &&
      entry.chainId === other.chainId &&
      entry.runId === other.runId &&
      entry.requestedAtMs === other.requestedAtMs
    );
  });
}

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function normalizeTaskPrefix(value: string): string {
  return value.trim().toUpperCase().slice(0, 2);
}

function parseExcludedModPrefixes(value: string): string[] {
  const prefixes: string[] = [];
  value
    .split(/[\s,;]+/)
    .map(normalizeTaskPrefix)
    .filter(Boolean)
    .forEach((prefix) => {
      if (!prefixes.includes(prefix)) prefixes.push(prefix);
    });
  return prefixes;
}

function formatPercent(value?: number | null): string {
  if (value === undefined || value === null) return '-';
  return `${Math.round(value * 1000) / 10}%`;
}

function formatPriorityScore(value?: number | null): string {
  if (value === undefined || value === null) return '-';
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatRelativeAge(ageSeconds?: number): string {
  if (ageSeconds === undefined) return '-';
  if (ageSeconds < 60) return 'just now';
  if (ageSeconds < 3600) return `${Math.floor(ageSeconds / 60)}m ago`;
  if (ageSeconds < 86400) return `${Math.floor(ageSeconds / 3600)}h ago`;
  return `${Math.floor(ageSeconds / 86400)}d ago`;
}

function formatDuration(durationMs?: number | null): string {
  if (durationMs === undefined || durationMs === null) return '-';
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  return `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s`;
}

function refreshRunDurationMs(
  run: Pick<RemoteDataCollectionRunResponse, 'createdAtUtc' | 'completedAtUtc'>
): number | null {
  if (!run.completedAtUtc) return null;
  const startedAt = Date.parse(run.createdAtUtc);
  const completedAt = Date.parse(run.completedAtUtc);
  if (!Number.isFinite(startedAt) || !Number.isFinite(completedAt)) return null;
  return Math.max(0, completedAt - startedAt);
}

function interpolateColor(
  start: [number, number, number],
  end: [number, number, number],
  ratio: number
): string {
  const clampedRatio = Math.max(0, Math.min(1, ratio));
  const [red, green, blue] = start.map((channel, index) =>
    Math.round(channel + (end[index] - channel) * clampedRatio)
  );
  return `rgb(${red}, ${green}, ${blue})`;
}

function snapshotAgeColor(ageSeconds: number): string {
  const oneHour = 60 * 60;
  const oneDay = 24 * oneHour;
  const twoDays = 2 * oneDay;

  if (ageSeconds <= oneHour) {
    return interpolateColor([65, 195, 91], [138, 183, 70], ageSeconds / oneHour);
  }
  if (ageSeconds <= oneDay) {
    return interpolateColor(
      [138, 183, 70],
      [238, 163, 55],
      (ageSeconds - oneHour) / (oneDay - oneHour)
    );
  }
  if (ageSeconds <= twoDays) {
    return interpolateColor([238, 163, 55], [232, 82, 63], (ageSeconds - oneDay) / oneDay);
  }
  return interpolateColor(
    [232, 82, 63],
    [255, 55, 85],
    Math.min(1, (ageSeconds - twoDays) / twoDays)
  );
}

function rowSortKey(
  row: RemoteDataCollectionRow
): [number, number, number, number, number, string, string] {
  const newest = row.newestCreatedAtUtc ? new Date(row.newestCreatedAtUtc).getTime() : 0;
  const priorityScore = row.priorityScore ?? row.flaggedCount;
  return [
    -priorityScore,
    -row.flaggedCount,
    -row.lowSimilarityCount,
    -row.lowDeltaCount,
    -newest,
    row.chainName,
    row.rowKey,
  ];
}

function sortRows(rows: RemoteDataCollectionRow[]): RemoteDataCollectionRow[] {
  return [...rows]
    .sort((a, b) => {
      const left = rowSortKey(a);
      const right = rowSortKey(b);
      for (let index = 0; index < left.length; index += 1) {
        if (left[index] < right[index]) return -1;
        if (left[index] > right[index]) return 1;
      }
      return 0;
    })
    .slice(0, ROW_LIMIT);
}

function sumChainRows(
  chain: RemoteDataCollectionChainSnapshot,
  selector: (row: RemoteDataCollectionRow) => number
): number {
  return (chain.rows ?? []).reduce((total, row) => total + selector(row), 0);
}

function recomputeDashboard(
  previous: RemoteDataCollectionDashboardResponse,
  updatedChain: RemoteDataCollectionChainSnapshot
): RemoteDataCollectionDashboardResponse {
  const chains = previous.chains.some((chain) => chain.chain.chainId === updatedChain.chain.chainId)
    ? previous.chains.map((chain) =>
        chain.chain.chainId === updatedChain.chain.chainId ? updatedChain : chain
      )
    : [...previous.chains, updatedChain];
  return {
    ...previous,
    chains,
    supportedChainIds: previous.supportedChainIds.includes(updatedChain.chain.chainId)
      ? previous.supportedChainIds
      : [...previous.supportedChainIds, updatedChain.chain.chainId].sort(
          (left, right) => left - right
        ),
    topRows: sortRows(chains.flatMap((chain) => chain.rows ?? [])),
  };
}

function searchParamsFromUrl(value: string): URLSearchParams | null {
  try {
    return new URL(value, window.location.origin).searchParams;
  } catch {
    const paramsText = value.split('?')[1];
    return paramsText ? new URLSearchParams(paramsText) : null;
  }
}

function remoteDataCollectionToolUrl(row: RemoteDataCollectionRow): string | null {
  let inferenceId: string | undefined = row.sampleInferenceIds[0];
  if (!inferenceId && row.masterDataCollectionUrl) {
    const params = searchParamsFromUrl(row.masterDataCollectionUrl);
    inferenceId = params?.get('inference') ?? undefined;
  }
  if (!inferenceId) return null;
  const params = new URLSearchParams({ chain: String(row.chainId), inference: inferenceId });
  return `${FORGE_REMOTE_DC_TOOL_PATH}?${params.toString()}`;
}

function applyRemoteDcDefaults(defaults: RemoteDataCollectionDefaults): {
  searchSize: number;
  sampleSize: number;
  similarityThresholdPct: number;
  deltaThresholdPct: number;
  ssmConfidenceThresholdPct: number;
  lowEmbeddingThreshold: number;
  includeSimilarityPriority: boolean;
  includeDeltaPriority: boolean;
  similarityPriorityWeight: number;
  deltaPriorityWeight: number;
  lowEmbeddingPriorityWeight: number;
  productPriorityWeight: number;
  cartonPriorityWeight: number;
  detectedPolygonNormalizationPct: number;
} {
  return {
    searchSize: defaults.searchSize ?? REMOTE_DC_DEFAULT_SEARCH_SIZE,
    sampleSize: defaults.sampleSize,
    similarityThresholdPct: Math.round(defaults.similarityThreshold * 100),
    deltaThresholdPct: Math.round(defaults.deltaThreshold * 100),
    ssmConfidenceThresholdPct: Math.round(
      (defaults.ssmConfidenceThreshold ?? REMOTE_DC_DEFAULT_SSM_CONFIDENCE_THRESHOLD_PCT / 100) *
        100
    ),
    lowEmbeddingThreshold:
      defaults.lowEmbeddingThreshold ?? REMOTE_DC_DEFAULT_LOW_EMBEDDING_THRESHOLD,
    includeSimilarityPriority:
      defaults.includeSimilarityPriority ?? REMOTE_DC_DEFAULT_INCLUDE_SIMILARITY_PRIORITY,
    includeDeltaPriority: defaults.includeDeltaPriority ?? REMOTE_DC_DEFAULT_INCLUDE_DELTA_PRIORITY,
    similarityPriorityWeight:
      defaults.similarityPriorityWeight ?? REMOTE_DC_DEFAULT_SIMILARITY_PRIORITY_WEIGHT,
    deltaPriorityWeight: defaults.deltaPriorityWeight ?? REMOTE_DC_DEFAULT_DELTA_PRIORITY_WEIGHT,
    lowEmbeddingPriorityWeight:
      defaults.lowEmbeddingPriorityWeight ?? REMOTE_DC_DEFAULT_LOW_EMBEDDING_PRIORITY_WEIGHT,
    productPriorityWeight:
      defaults.productPriorityWeight ?? REMOTE_DC_DEFAULT_PRODUCT_PRIORITY_WEIGHT,
    cartonPriorityWeight: defaults.cartonPriorityWeight ?? REMOTE_DC_DEFAULT_CARTON_PRIORITY_WEIGHT,
    detectedPolygonNormalizationPct: Math.round(
      (defaults.detectedPolygonNormalization ??
        REMOTE_DC_DEFAULT_DETECTED_POLYGON_NORMALIZATION_PCT / 100) * 100
    ),
  };
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function minDelta(row: RemoteDataCollectionRow): number | null | undefined {
  return row.minDelta ?? row.maxDelta;
}

function rowMatchesTaskPrefix(row: RemoteDataCollectionRow, taskPrefix: string): boolean {
  if (!taskPrefix) return true;
  return (row.taskPrefixes ?? []).includes(taskPrefix);
}

function formatTaskPrefixes(row: RemoteDataCollectionRow): string {
  const prefixes = row.taskPrefixes ?? [];
  return prefixes.length > 0 ? prefixes.join(', ') : '-';
}

function cameraRowText(row: RemoteDataCollectionRow): { title: string; detail: string } {
  const imageId = row.sampleImageIds[0];
  const storeName = row.sampleStoreNames[0];
  const title = row.cameraId
    ? `Camera ${row.cameraId}`
    : imageId !== undefined
      ? `Image ${imageId}`
      : 'Camera image';
  const detail = [storeName, row.cameraId && imageId !== undefined ? `Image ${imageId}` : null]
    .filter(Boolean)
    .join(' · ');
  return { title, detail };
}

const headerCellStyle: React.CSSProperties = {
  padding: '5px 10px',
  textAlign: 'left',
  fontSize: 10,
  color: 'var(--text-tertiary)',
  fontWeight: 800,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--divider)',
  verticalAlign: 'middle',
};

const bodyCellStyle: React.CSSProperties = {
  padding: '7px 10px',
  borderBottom: '1px solid var(--divider)',
  color: 'var(--text-secondary)',
  fontSize: 12,
  verticalAlign: 'middle',
};

const monoCellStyle: React.CSSProperties = {
  ...bodyCellStyle,
  fontFamily: 'var(--font-mono)',
};

const actionHeaderCellStyle: React.CSSProperties = {
  ...headerCellStyle,
  width: 126,
};

const actionCellStyle: React.CSSProperties = {
  ...bodyCellStyle,
  width: 126,
};

const controlStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  color: 'var(--text-primary)',
  borderRadius: 6,
  height: 22,
  padding: '0 6px',
  boxSizing: 'border-box',
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1.1,
};

const checkboxControlStyle: React.CSSProperties = {
  ...controlStyle,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  width: '100%',
  cursor: 'pointer',
};

interface DashboardRangeControlProps {
  label: string;
  value: number;
  unit?: string;
  min: number;
  max: number;
  step?: number;
  disabled?: boolean;
  headerSlot?: React.ReactNode;
  onChange?: (value: number) => void;
}

function DashboardRangeControl({
  label,
  value,
  unit = '',
  min,
  max,
  step = 1,
  disabled = false,
  headerSlot,
  onChange,
}: DashboardRangeControlProps) {
  const [draftValue, setDraftValue] = useState(String(value));
  const controlId = `rdc-${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')}`;

  useEffect(() => {
    setDraftValue(String(value));
  }, [value]);

  const commitDraftValue = useCallback(() => {
    const parsedValue = parseRangeDraftValue(draftValue);
    if (parsedValue === null || !onChange) {
      setDraftValue(String(value));
      return;
    }
    const nextValue = snapRangeValue(parsedValue, min, max, step);
    setDraftValue(String(nextValue));
    onChange(nextValue);
  }, [draftValue, max, min, onChange, step, value]);

  const handleDraftValueChange = useCallback(
    (nextDraftValue: string) => {
      setDraftValue(nextDraftValue);
      const parsedValue = parseRangeDraftValue(nextDraftValue);
      if (parsedValue !== null && isRangeValueStepAligned(parsedValue, min, max, step)) {
        onChange?.(snapRangeValue(parsedValue, min, max, step));
      }
    },
    [max, min, onChange, step]
  );

  return (
    <div className="mdc-range-control rdc-dashboard-range-control">
      <div className="mdc-range-header">
        <label className="mdc-range-label" htmlFor={controlId}>
          {label}
        </label>
        <span className="rdc-dashboard-range-header-actions">
          {headerSlot}
          <span className="mdc-value-pill rdc-range-value-editor">
            <input
              aria-label={`${label} value`}
              className="rdc-range-value-input"
              type="number"
              min={min}
              max={max}
              step={step}
              value={draftValue}
              disabled={disabled}
              onChange={(event) => handleDraftValueChange(event.target.value)}
              onBlur={commitDraftValue}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitDraftValue();
                }
              }}
            />
            {unit ? <span className="rdc-range-value-unit">{unit}</span> : null}
          </span>
        </span>
      </div>
      <input
        id={controlId}
        className="mdc-range-input"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        aria-label={label}
        onChange={(event) => onChange?.(Number(event.target.value))}
      />
    </div>
  );
}

function DashboardFieldControl({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mdc-range-control rdc-dashboard-field-control">
      <div className="mdc-range-header">
        <span className="mdc-range-label">{label}</span>
      </div>
      {children}
    </div>
  );
}

function DashboardInlinePriorityToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="mdc-range-toggle rdc-dashboard-inline-priority-toggle">
      <input
        type="checkbox"
        aria-label={label}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      Priority
    </label>
  );
}

function RemoteDataCollectionDetailsDialog({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="mdc-modal-backdrop"
      data-testid="remote-dc-priority-details-dialog"
      onClick={onClose}
    >
      <div
        className="mdc-modal rdc-priority-details-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rdc-priority-details-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mdc-modal-header">
          <div>
            <h2 id="rdc-priority-details-title">Remote DC scoring details</h2>
          </div>
          <button type="button" className="mdc-modal-close" aria-label="Close" onClick={onClose}>
            <CloseOutlinedIcon style={{ fontSize: 18 }} />
          </button>
        </div>
        <div className="mdc-modal-body rdc-priority-details-body">
          <section className="rdc-priority-details-section">
            <h3>Task selection</h3>
            <dl>
              <dt>Searched</dt>
              <dd>How many recent inferences to scan for the selected chain.</dd>
              <dt>Tasks</dt>
              <dd>Maximum number of task rows kept after scoring and sorting.</dd>
              <dt>Window</dt>
              <dd>Limits the scan to recent inferences. Defaults to Last 1 day.</dd>
              <dt>Tag prefix</dt>
              <dd>
                Optional task prefix filter. When set, searched counts matching-prefix inferences;
                the scan can skip non-matching inferences until that count is reached or the window
                ends.
              </dd>
            </dl>
          </section>
          <section className="rdc-priority-details-section">
            <h3>Flagging thresholds</h3>
            <dl>
              <dt>Sim &lt;</dt>
              <dd>Flags detections whose top candidate similarity is below the threshold.</dd>
              <dt>Delta &lt;</dt>
              <dd>
                Flags ambiguous detections where the top candidate is too close to the next one.
              </dd>
              <dt>SSM &gt;=</dt>
              <dd>Only detections at or above this confidence can become tasks.</dd>
              <dt># Emb &lt;=</dt>
              <dd>
                Counts a POG candidate as low-embedding when its active embedding count is at or
                below this value.
              </dd>
            </dl>
          </section>
          <section className="rdc-priority-details-section">
            <h3>Priority score</h3>
            <p>
              Thresholds decide which detections are flagged. Priority options decide which flagged
              image rows rise to the top.
            </p>
            <dl>
              <dt>Sim priority</dt>
              <dd>When checked, low-similarity detections add the Sim weight to priority.</dd>
              <dt>Delta priority</dt>
              <dd>When checked, low-delta detections add the Delta weight to priority.</dd>
              <dt>Low emb weight</dt>
              <dd>Added when the top POG candidate has # Emb &lt;= the selected threshold.</dd>
              <dt>Product / Carton weight</dt>
              <dd>Multiplier applied to the detection contribution based on polygon type.</dd>
              <dt>Normalize</dt>
              <dd>
                Divides the row score by detected boxes to make sparse images more competitive.
              </dd>
            </dl>
            <div className="rdc-priority-formula">
              priority = sum(flagged polygon contributions) / detected boxes ^ normalize
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

const refreshButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-secondary)',
  borderRadius: 6,
  height: 30,
  padding: '0 10px',
  fontSize: 11,
  fontWeight: 800,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const linkButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  color: 'var(--accent)',
  fontWeight: 800,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const REMOTE_DC_URL_FILTERS = {
  chain: chainFilterParam(),
  task: stringParam(),
};

export const RemoteDataCollectionDashboard: React.FC = () => {
  const { token, isNonAdminPreview } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [dashboard, setDashboard] = useState<RemoteDataCollectionDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [activeRuns, setActiveRuns] = useState<ActiveRemoteDataCollectionRun[]>(
    readActiveRemoteDataCollectionRuns
  );
  const [startingChainIds, setStartingChainIds] = useState<Set<number>>(() => new Set());
  const [startingRefreshTimesMs, setStartingRefreshTimesMs] = useState<Record<number, number>>({});
  const [refreshDurationsMs, setRefreshDurationsMs] = useState<Record<number, number>>({});
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [filters, updateFilters] = useUrlFilterState(REMOTE_DC_URL_FILTERS);
  const { chain: topRowsChainFilter, task: taskPrefixFilter } = filters;
  const [searchSize, setSearchSize] = useState(REMOTE_DC_DEFAULT_SEARCH_SIZE);
  const [sampleSize, setSampleSize] = useState(REMOTE_DC_DEFAULT_SAMPLE_SIZE);
  const [lookbackHours, setLookbackHours] = useState<number | null>(
    REMOTE_DC_DEFAULT_LOOKBACK_HOURS
  );
  const [modPrefix, setModPrefix] = useState('');
  const [excludedModPrefixes, setExcludedModPrefixes] = useState('');
  const [skipInferencesWithoutPogs, setSkipInferencesWithoutPogs] = useState(
    REMOTE_DC_DEFAULT_SKIP_INFERENCES_WITHOUT_POGS
  );
  const [unstructuredPogFilter, setUnstructuredPogFilter] =
    useState<RemoteDataCollectionUnstructuredPogFilter>(
      REMOTE_DC_DEFAULT_UNSTRUCTURED_POG_FILTER as RemoteDataCollectionUnstructuredPogFilter
    );
  const [similarityThresholdPct, setSimilarityThresholdPct] = useState(
    REMOTE_DC_DEFAULT_SIMILARITY_THRESHOLD_PCT
  );
  const [deltaThresholdPct, setDeltaThresholdPct] = useState(REMOTE_DC_DEFAULT_DELTA_THRESHOLD_PCT);
  const [ssmConfidenceThresholdPct, setSsmConfidenceThresholdPct] = useState(
    REMOTE_DC_DEFAULT_SSM_CONFIDENCE_THRESHOLD_PCT
  );
  const [lowEmbeddingThreshold, setLowEmbeddingThreshold] = useState(
    REMOTE_DC_DEFAULT_LOW_EMBEDDING_THRESHOLD
  );
  const [includeSimilarityPriority, setIncludeSimilarityPriority] = useState(
    REMOTE_DC_DEFAULT_INCLUDE_SIMILARITY_PRIORITY
  );
  const [includeDeltaPriority, setIncludeDeltaPriority] = useState(
    REMOTE_DC_DEFAULT_INCLUDE_DELTA_PRIORITY
  );
  const [similarityPriorityWeight, setSimilarityPriorityWeight] = useState(
    REMOTE_DC_DEFAULT_SIMILARITY_PRIORITY_WEIGHT
  );
  const [deltaPriorityWeight, setDeltaPriorityWeight] = useState(
    REMOTE_DC_DEFAULT_DELTA_PRIORITY_WEIGHT
  );
  const [lowEmbeddingPriorityWeight, setLowEmbeddingPriorityWeight] = useState(
    REMOTE_DC_DEFAULT_LOW_EMBEDDING_PRIORITY_WEIGHT
  );
  const [productPriorityWeight, setProductPriorityWeight] = useState(
    REMOTE_DC_DEFAULT_PRODUCT_PRIORITY_WEIGHT
  );
  const [cartonPriorityWeight, setCartonPriorityWeight] = useState(
    REMOTE_DC_DEFAULT_CARTON_PRIORITY_WEIGHT
  );
  const [detectedPolygonNormalizationPct, setDetectedPolygonNormalizationPct] = useState(
    REMOTE_DC_DEFAULT_DETECTED_POLYGON_NORMALIZATION_PCT
  );
  const pollErrorRunIdsRef = useRef<Set<string>>(new Set());
  const refreshStartedAtMsRef = useRef<Record<number, number>>({});
  const refreshingChainIds = useMemo(
    () => new Set([...activeRuns.map((run) => run.chainId), ...startingChainIds]),
    [activeRuns, startingChainIds]
  );
  const refreshStartedAtMsByChainId = useMemo(() => {
    const next = new Map<number, number>();
    activeRuns.forEach((run) => {
      next.set(run.chainId, run.requestedAtMs);
    });
    Object.entries(startingRefreshTimesMs).forEach(([chainId, startedAtMs]) => {
      next.set(Number(chainId), startedAtMs);
    });
    return next;
  }, [activeRuns, startingRefreshTimesMs]);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    writeActiveRemoteDataCollectionRuns(activeRuns);
  }, [activeRuns]);

  useEffect(() => {
    if (refreshingChainIds.size === 0) return undefined;
    setNowMs(Date.now());
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [refreshingChainIds.size]);

  const rememberRefreshStartedAt = useCallback((chainIds: number[], startedAtMs: number) => {
    const next = { ...refreshStartedAtMsRef.current };
    chainIds.forEach((chainId) => {
      next[chainId] = startedAtMs;
    });
    refreshStartedAtMsRef.current = next;
    setStartingRefreshTimesMs(next);
  }, []);

  const forgetRefreshStartedAt = useCallback((chainIds: number[]) => {
    const next = { ...refreshStartedAtMsRef.current };
    chainIds.forEach((chainId) => {
      delete next[chainId];
    });
    refreshStartedAtMsRef.current = next;
    setStartingRefreshTimesMs(next);
  }, []);

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    try {
      const [nextDashboard, defaults] = await Promise.all([
        ForgeApiService.fetchRemoteDataCollection(token),
        ForgeApiService.fetchRemoteDataCollectionConfig(token, {
          nonAdminPreview: isNonAdminPreview,
        }),
      ]);
      const appliedDefaults = applyRemoteDcDefaults(defaults);
      setSearchSize(appliedDefaults.searchSize);
      setSampleSize(appliedDefaults.sampleSize);
      setSimilarityThresholdPct(appliedDefaults.similarityThresholdPct);
      setDeltaThresholdPct(appliedDefaults.deltaThresholdPct);
      setSsmConfidenceThresholdPct(appliedDefaults.ssmConfidenceThresholdPct);
      setLowEmbeddingThreshold(appliedDefaults.lowEmbeddingThreshold);
      setIncludeSimilarityPriority(appliedDefaults.includeSimilarityPriority);
      setIncludeDeltaPriority(appliedDefaults.includeDeltaPriority);
      setSimilarityPriorityWeight(appliedDefaults.similarityPriorityWeight);
      setDeltaPriorityWeight(appliedDefaults.deltaPriorityWeight);
      setLowEmbeddingPriorityWeight(appliedDefaults.lowEmbeddingPriorityWeight);
      setProductPriorityWeight(appliedDefaults.productPriorityWeight);
      setCartonPriorityWeight(appliedDefaults.cartonPriorityWeight);
      setDetectedPolygonNormalizationPct(appliedDefaults.detectedPolygonNormalizationPct);
      setLoadError(null);
      setDashboard({
        ...nextDashboard,
        topRows: sortRows(nextDashboard.topRows ?? []),
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Error loading remote data collection';
      setLoadError(message);
      setDashboard(null);
      showErrorToast(message);
    } finally {
      setIsLoading(false);
    }
  }, [isNonAdminPreview, showErrorToast, token]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const chains = useMemo(() => dashboard?.chains ?? [], [dashboard?.chains]);
  const chainOptions = useMemo(() => {
    const supportedChainIds = new Set(
      dashboard?.supportedChainIds ?? chains.map((chain) => chain.chain.chainId)
    );
    return [...supportedChainIds]
      .sort((left, right) => left - right)
      .map((chainId) => {
        const loadedChain = chains.find((chain) => chain.chain.chainId === chainId);
        const configuredChain = CHAIN_OPTIONS.find((chain) => chain.id === chainId);
        return {
          chainId,
          label: loadedChain?.chain.chainName ?? configuredChain?.label ?? `Chain ${chainId}`,
        };
      });
  }, [chains, dashboard?.supportedChainIds]);
  const exportChainId = useMemo(() => {
    if (topRowsChainFilter !== 'all') return topRowsChainFilter;
    return chainOptions.length === 1 ? chainOptions[0].chainId : null;
  }, [chainOptions, topRowsChainFilter]);

  const refreshRequest = useCallback(
    (chainId: number): RemoteDataCollectionRefreshRequest => ({
      chain_id: chainId,
      search_size: searchSize,
      sample_size: sampleSize,
      lookback_hours: lookbackHours,
      mod_prefix: normalizeTaskPrefix(modPrefix) || null,
      excluded_mod_prefixes: parseExcludedModPrefixes(excludedModPrefixes),
      skip_inferences_without_pogs: skipInferencesWithoutPogs,
      unstructured_pog_filter: unstructuredPogFilter,
      similarity_threshold: similarityThresholdPct / 100,
      delta_threshold: deltaThresholdPct / 100,
      ssm_confidence_threshold: ssmConfidenceThresholdPct / 100,
      merge_linked_candidates: DASHBOARD_MERGE_LINKED_CANDIDATES,
      low_embedding_threshold: lowEmbeddingThreshold,
      include_similarity_priority: includeSimilarityPriority,
      include_delta_priority: includeDeltaPriority,
      similarity_priority_weight: similarityPriorityWeight,
      delta_priority_weight: deltaPriorityWeight,
      low_embedding_priority_weight: lowEmbeddingPriorityWeight,
      product_priority_weight: productPriorityWeight,
      carton_priority_weight: cartonPriorityWeight,
      detected_polygon_normalization: detectedPolygonNormalizationPct / 100,
    }),
    [
      cartonPriorityWeight,
      detectedPolygonNormalizationPct,
      deltaThresholdPct,
      deltaPriorityWeight,
      includeDeltaPriority,
      includeSimilarityPriority,
      excludedModPrefixes,
      lookbackHours,
      lowEmbeddingPriorityWeight,
      lowEmbeddingThreshold,
      modPrefix,
      productPriorityWeight,
      sampleSize,
      searchSize,
      similarityPriorityWeight,
      similarityThresholdPct,
      skipInferencesWithoutPogs,
      ssmConfidenceThresholdPct,
      unstructuredPogFilter,
    ]
  );

  const applyCompletedRunSnapshots = useCallback(
    (completedRuns: RemoteDataCollectionRunResponse[]) => {
      const updatedChains = completedRuns.flatMap((run) => (run.snapshot ? [run.snapshot] : []));
      if (updatedChains.length === 0) return;
      const completedAtMs = Date.now();
      const completedDurationsMs = completedRuns.reduce<Record<number, number>>(
        (durations, run) => {
          if (!run.snapshot) return durations;
          const savedDurationMs =
            run.snapshot.snapshot?.computeDurationMs ?? refreshRunDurationMs(run);
          const locallyStartedAtMs = refreshStartedAtMsRef.current[run.chainId];
          const observedDurationMs =
            locallyStartedAtMs === undefined
              ? null
              : Math.max(0, completedAtMs - locallyStartedAtMs);
          const durationMs =
            observedDurationMs === null
              ? savedDurationMs
              : Math.max(savedDurationMs ?? 0, observedDurationMs);
          if (durationMs !== null && durationMs !== undefined) {
            durations[run.chainId] = durationMs;
          }
          return durations;
        },
        {}
      );
      setDashboard((current) =>
        updatedChains.reduce<RemoteDataCollectionDashboardResponse | null>(
          (nextDashboard, updatedChain) =>
            nextDashboard
              ? recomputeDashboard(nextDashboard, updatedChain)
              : {
                  chains: [updatedChain],
                  topRows: sortRows(updatedChain.rows ?? []),
                  supportedChainIds: [updatedChain.chain.chainId],
                },
          current
        )
      );
      setLoadError(null);
      setRefreshDurationsMs((current) => {
        const next = { ...current };
        Object.entries(completedDurationsMs).forEach(([chainId, durationMs]) => {
          next[Number(chainId)] = durationMs;
        });
        return Object.keys(completedDurationsMs).length > 0 ? next : current;
      });
    },
    []
  );

  const upsertActiveRun = useCallback(
    (run: RemoteDataCollectionRunResponse) => {
      const requestedAtMs = refreshRunCreatedAtMs(run.createdAtUtc);
      if (isForgeRefreshRunTimedOut(requestedAtMs)) {
        showErrorToast('Remote data collection refresh timed out. Start a new refresh to retry.');
        return;
      }
      setActiveRuns((current) => {
        const next = [
          ...current.filter((activeRun) => activeRun.chainId !== run.chainId),
          {
            chainId: run.chainId,
            runId: run.runId,
            requestedAtMs,
          },
        ];
        return sameActiveRemoteDataCollectionRuns(current, next) ? current : next;
      });
    },
    [showErrorToast]
  );

  const startRefreshRun = useCallback(
    async (chainId: number) => {
      const run = await ForgeApiService.createRemoteDataCollectionRun(
        token,
        refreshRequest(chainId)
      );
      pollErrorRunIdsRef.current.delete(run.runId);
      if (run.status === 'completed') {
        applyCompletedRunSnapshots([run]);
        return run;
      }
      if (run.status === 'failed') {
        showErrorToast(run.error || `Remote data collection refresh failed for chain ${chainId}`);
        return run;
      }
      upsertActiveRun(run);
      return run;
    },
    [applyCompletedRunSnapshots, refreshRequest, showErrorToast, token, upsertActiveRun]
  );

  const handleRefreshChain = async (chainId: number) => {
    setStartingChainIds((current) => new Set(current).add(chainId));
    rememberRefreshStartedAt([chainId], Date.now());
    let run: RemoteDataCollectionRunResponse | null = null;
    try {
      run = await startRefreshRun(chainId);
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Error refreshing remote data collection'
      );
    } finally {
      setStartingChainIds((current) => {
        const next = new Set(current);
        next.delete(chainId);
        return next;
      });
      if (run?.status !== 'running') {
        forgetRefreshStartedAt([chainId]);
      }
    }
  };

  const handleRefreshAllChains = async () => {
    const chainIds = chainOptions.map((chain) => chain.chainId);
    if (chainIds.length === 0) return;
    if (!window.confirm(REFRESH_ALL_CONFIRMATION_MESSAGE)) return;

    setStartingChainIds((current) => new Set([...current, ...chainIds]));
    const startedAtMs = Date.now();
    rememberRefreshStartedAt(chainIds, startedAtMs);
    let results: PromiseSettledResult<RemoteDataCollectionRunResponse>[] = [];
    try {
      results = await Promise.allSettled(chainIds.map((chainId) => startRefreshRun(chainId)));
      const failedCount =
        results.length - results.filter((result) => result.status === 'fulfilled').length;
      if (failedCount > 0) {
        showErrorToast(
          `Remote data collection refresh failed to start for ${failedCount} of ${chainIds.length} chains`
        );
      }
    } finally {
      setStartingChainIds((current) => {
        const next = new Set(current);
        chainIds.forEach((chainId) => next.delete(chainId));
        return next;
      });
      const runningChainIds = new Set(
        results.flatMap((result) =>
          result.status === 'fulfilled' && result.value.status === 'running'
            ? [result.value.chainId]
            : []
        )
      );
      forgetRefreshStartedAt(chainIds.filter((chainId) => !runningChainIds.has(chainId)));
    }
  };

  useEffect(() => {
    if (!token || activeRuns.length === 0) return undefined;
    let isCancelled = false;
    const abortController = new AbortController();

    const pollRuns = async () => {
      const runsAtPollStart = activeRuns;
      const nowMs = Date.now();
      const timedOutRuns = runsAtPollStart.filter((run) =>
        isForgeRefreshRunTimedOut(run.requestedAtMs, nowMs)
      );
      const pollableRuns = runsAtPollStart.filter((run) =>
        isForgeRefreshRunFresh(run.requestedAtMs, nowMs)
      );
      const results = await Promise.allSettled(
        pollableRuns.map((run) =>
          ForgeApiService.fetchRemoteDataCollectionRun(token, run.runId, {
            signal: abortController.signal,
          })
        )
      );
      if (isCancelled) return;

      const completedRuns: RemoteDataCollectionRunResponse[] = [];
      const failedRuns: RemoteDataCollectionRunResponse[] = [];
      const stillRunningRuns: ActiveRemoteDataCollectionRun[] = [];
      const terminalErroredRuns: ActiveRemoteDataCollectionRun[] = [];

      results.forEach((result, index) => {
        const activeRun = pollableRuns[index];
        if (result.status === 'rejected') {
          if (isTerminalForgeRefreshRunPollError(result.reason)) {
            terminalErroredRuns.push(activeRun);
          } else {
            stillRunningRuns.push(activeRun);
          }
          if (!pollErrorRunIdsRef.current.has(activeRun.runId)) {
            pollErrorRunIdsRef.current.add(activeRun.runId);
            showErrorToast(
              result.reason instanceof Error
                ? result.reason.message
                : `Error checking Remote DC run ${activeRun.runId}`
            );
          }
          return;
        }

        pollErrorRunIdsRef.current.delete(activeRun.runId);
        if (result.value.status === 'completed') {
          completedRuns.push(result.value);
        } else if (result.value.status === 'failed') {
          failedRuns.push(result.value);
        } else {
          stillRunningRuns.push(activeRun);
        }
      });

      if (completedRuns.length > 0) {
        applyCompletedRunSnapshots(completedRuns);
      }
      if (failedRuns.length > 0) {
        const chainLabel =
          failedRuns.length === 1
            ? `chain ${failedRuns[0].chainId}`
            : `${failedRuns.length} chains`;
        showErrorToast(`Remote data collection refresh failed for ${chainLabel}`);
      }
      if (timedOutRuns.length > 0) {
        const chainLabel =
          timedOutRuns.length === 1
            ? `chain ${timedOutRuns[0].chainId}`
            : `${timedOutRuns.length} chains`;
        showErrorToast(
          `Remote data collection refresh timed out for ${chainLabel}. Start a new refresh to retry.`
        );
        timedOutRuns.forEach((run) => pollErrorRunIdsRef.current.delete(run.runId));
      }
      if (terminalErroredRuns.length > 0) {
        terminalErroredRuns.forEach((run) => pollErrorRunIdsRef.current.delete(run.runId));
      }
      const finishedChainIds = [
        ...completedRuns.map((run) => run.chainId),
        ...failedRuns.map((run) => run.chainId),
        ...timedOutRuns.map((run) => run.chainId),
        ...terminalErroredRuns.map((run) => run.chainId),
      ];
      if (finishedChainIds.length > 0) {
        forgetRefreshStartedAt(finishedChainIds);
      }

      if (
        completedRuns.length > 0 ||
        failedRuns.length > 0 ||
        timedOutRuns.length > 0 ||
        terminalErroredRuns.length > 0
      ) {
        setActiveRuns((current) => {
          const polledRunIds = new Set(runsAtPollStart.map((run) => run.runId));
          const newRunsStartedDuringPoll = current.filter((run) => !polledRunIds.has(run.runId));
          const next = [...newRunsStartedDuringPoll, ...stillRunningRuns];
          return sameActiveRemoteDataCollectionRuns(current, next) ? current : next;
        });
      }
    };

    void pollRuns();
    const intervalId = window.setInterval(() => {
      void pollRuns();
    }, 5000);
    return () => {
      isCancelled = true;
      abortController.abort();
      window.clearInterval(intervalId);
    };
  }, [activeRuns, applyCompletedRunSnapshots, forgetRefreshStartedAt, showErrorToast, token]);

  const handleExportRows = async () => {
    if (exportChainId === null) {
      showErrorToast('Select one chain before exporting Remote DC CSV');
      return;
    }
    setIsExporting(true);
    try {
      const normalizedTaskPrefix = normalizeTaskPrefix(taskPrefixFilter);
      const { blob, filename } = await ForgeApiService.exportRemoteDataCollectionWorkItems(token, {
        chainId: exportChainId,
        taskPrefix: normalizedTaskPrefix || null,
      });
      downloadBlob(blob, filename);
      showSuccessToast('Remote data collection CSV exported');
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error exporting Remote DC rows');
    } finally {
      setIsExporting(false);
    }
  };

  const topRows = useMemo(() => {
    if (!dashboard) return [];
    const rows =
      topRowsChainFilter === 'all'
        ? dashboard.chains.flatMap((chain) => chain.rows ?? [])
        : (dashboard.chains.find((chain) => chain.chain.chainId === topRowsChainFilter)?.rows ??
          []);
    return sortRows(
      rows.filter((row) => rowMatchesTaskPrefix(row, normalizeTaskPrefix(taskPrefixFilter)))
    );
  }, [dashboard, taskPrefixFilter, topRowsChainFilter]);
  const isRefreshingAnyChain = refreshingChainIds.size > 0;
  const isExportDisabled = isExporting || isLoading || Boolean(loadError) || exportChainId === null;
  const longestRunningDurationMs = useMemo(() => {
    if (refreshingChainIds.size === 0) return null;
    return [...refreshingChainIds].reduce<number | null>((longestDurationMs, chainId) => {
      const startedAtMs = refreshStartedAtMsByChainId.get(chainId);
      if (startedAtMs === undefined) return longestDurationMs;
      const durationMs = Math.max(0, nowMs - startedAtMs);
      return longestDurationMs === null ? durationMs : Math.max(longestDurationMs, durationMs);
    }, null);
  }, [nowMs, refreshStartedAtMsByChainId, refreshingChainIds]);

  return (
    <div className="page-content page-content--wide rdc-dashboard-page">
      <div className="forge-card rdc-dashboard-hero">
        <div className="rdc-dashboard-hero-grid">
          <div className="rdc-dashboard-title-block">
            <div>
              <div className="rdc-dashboard-title">Remote Data Collection</div>
              <div className="rdc-dashboard-copy">
                Refresh Remote DC task-list snapshots and open selected low-similarity or low-delta
                candidates in the labeling tool.
              </div>
            </div>
            <button
              type="button"
              className="rdc-dashboard-details-button"
              aria-haspopup="dialog"
              onClick={() => setIsDetailsOpen(true)}
            >
              <InfoOutlinedIcon style={{ fontSize: 15 }} />
              Details
            </button>
          </div>
          <div className="rdc-dashboard-controls">
            <DashboardRangeControl
              label="Searched"
              value={searchSize}
              min={1}
              max={REMOTE_DC_MAX_SEARCH_SIZE}
              onChange={setSearchSize}
            />
            <DashboardRangeControl
              label="Tasks"
              value={sampleSize}
              min={1}
              max={REMOTE_DC_MAX_SAMPLE_SIZE}
              onChange={setSampleSize}
            />
            <DashboardFieldControl label="Window">
              <select
                aria-label="Remote data collection time limit"
                value={lookbackHours ?? ''}
                onChange={(event) =>
                  setLookbackHours(event.target.value ? Number(event.target.value) : null)
                }
                style={{ ...controlStyle, width: '100%' }}
              >
                {LOOKBACK_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </DashboardFieldControl>
            <DashboardFieldControl label="Tag prefix">
              <input
                aria-label="Remote data collection tag prefix"
                value={modPrefix}
                maxLength={8}
                onChange={(event) => setModPrefix(event.target.value.toUpperCase())}
                style={{ ...controlStyle, width: '100%' }}
              />
            </DashboardFieldControl>
            <DashboardFieldControl label="Exclude prefixes">
              <input
                aria-label="Remote data collection excluded tag prefixes"
                value={excludedModPrefixes}
                maxLength={64}
                onChange={(event) => setExcludedModPrefixes(event.target.value.toUpperCase())}
                placeholder="MT, BK"
                style={{ ...controlStyle, width: '100%' }}
              />
            </DashboardFieldControl>
            <DashboardFieldControl label="No POGs">
              <label style={checkboxControlStyle}>
                <input
                  type="checkbox"
                  aria-label="Skip inferences with no POG"
                  checked={skipInferencesWithoutPogs}
                  onChange={(event) => setSkipInferencesWithoutPogs(event.target.checked)}
                  style={{ width: 12, height: 12, margin: 0 }}
                />
                <span>Skip</span>
              </label>
            </DashboardFieldControl>
            <DashboardFieldControl label="Unstructured">
              <select
                aria-label="Remote data collection unstructured POG filter"
                value={unstructuredPogFilter}
                onChange={(event) =>
                  setUnstructuredPogFilter(
                    event.target.value as RemoteDataCollectionUnstructuredPogFilter
                  )
                }
                style={{ ...controlStyle, width: '100%' }}
              >
                {UNSTRUCTURED_POG_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </DashboardFieldControl>
            <DashboardRangeControl
              label="Sim <"
              value={similarityThresholdPct}
              unit="%"
              min={0}
              max={100}
              headerSlot={
                <DashboardInlinePriorityToggle
                  label="Sim priority"
                  checked={includeSimilarityPriority}
                  onChange={setIncludeSimilarityPriority}
                />
              }
              onChange={setSimilarityThresholdPct}
            />
            <DashboardRangeControl
              label="Delta <"
              value={deltaThresholdPct}
              unit="%"
              min={0}
              max={100}
              headerSlot={
                <DashboardInlinePriorityToggle
                  label="Delta priority"
                  checked={includeDeltaPriority}
                  onChange={setIncludeDeltaPriority}
                />
              }
              onChange={setDeltaThresholdPct}
            />
            <DashboardRangeControl
              label="SSM >="
              value={ssmConfidenceThresholdPct}
              unit="%"
              min={0}
              max={100}
              onChange={setSsmConfidenceThresholdPct}
            />
            <DashboardRangeControl
              label="# Emb <="
              value={lowEmbeddingThreshold}
              min={0}
              max={20}
              onChange={setLowEmbeddingThreshold}
            />
            <DashboardRangeControl
              label="Sim weight"
              value={similarityPriorityWeight}
              unit="x"
              min={0}
              max={5}
              step={0.25}
              onChange={setSimilarityPriorityWeight}
            />
            <DashboardRangeControl
              label="Delta weight"
              value={deltaPriorityWeight}
              unit="x"
              min={0}
              max={5}
              step={0.25}
              onChange={setDeltaPriorityWeight}
            />
            <DashboardRangeControl
              label="Low emb weight"
              value={lowEmbeddingPriorityWeight}
              unit="x"
              min={0}
              max={5}
              step={0.25}
              onChange={setLowEmbeddingPriorityWeight}
            />
            <DashboardRangeControl
              label="Product weight"
              value={productPriorityWeight}
              unit="x"
              min={0}
              max={5}
              step={0.25}
              onChange={setProductPriorityWeight}
            />
            <DashboardRangeControl
              label="Carton weight"
              value={cartonPriorityWeight}
              unit="x"
              min={0}
              max={5}
              step={0.25}
              onChange={setCartonPriorityWeight}
            />
            <DashboardRangeControl
              label="Normalize"
              value={detectedPolygonNormalizationPct}
              unit="%"
              min={0}
              max={100}
              step={5}
              onChange={setDetectedPolygonNormalizationPct}
            />
          </div>
        </div>
      </div>
      {isDetailsOpen ? (
        <RemoteDataCollectionDetailsDialog onClose={() => setIsDetailsOpen(false)} />
      ) : null}

      <div className="forge-card rdc-dashboard-card">
        <div className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title">
          <div>Chains</div>
          <button
            type="button"
            style={{
              ...refreshButtonStyle,
              opacity: isLoading || loadError || isRefreshingAnyChain ? 0.65 : 1,
              cursor: isLoading || loadError || isRefreshingAnyChain ? 'not-allowed' : 'pointer',
            }}
            disabled={isLoading || Boolean(loadError) || isRefreshingAnyChain}
            onClick={() => void handleRefreshAllChains()}
          >
            <RefreshOutlinedIcon style={{ fontSize: 15 }} />
            {isRefreshingAnyChain && longestRunningDurationMs !== null
              ? `Refreshing ${formatDuration(longestRunningDurationMs)}`
              : 'Refresh all'}
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Chain</th>
                <th style={headerCellStyle}>Snapshot</th>
                <th style={headerCellStyle}>Searched</th>
                <th style={headerCellStyle}>Tasks</th>
                <th style={headerCellStyle}>Flagged</th>
                <th style={headerCellStyle}>Products</th>
                <th style={headerCellStyle}>Cartons</th>
                <th style={actionHeaderCellStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} style={bodyCellStyle}>
                    Loading snapshots...
                  </td>
                </tr>
              ) : loadError ? (
                <tr>
                  <td colSpan={8} style={bodyCellStyle}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        flexWrap: 'wrap',
                      }}
                    >
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 850 }}>
                          Remote Data Collection snapshots could not be loaded
                        </div>
                        <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 3 }}>
                          {loadError}
                        </div>
                      </div>
                      <button
                        type="button"
                        style={refreshButtonStyle}
                        onClick={() => void loadDashboard()}
                      >
                        <RefreshOutlinedIcon style={{ fontSize: 15 }} />
                        Retry
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                chains.map((chain) => {
                  const isRefreshing = refreshingChainIds.has(chain.chain.chainId);
                  const runningDurationMs = isRefreshing
                    ? refreshStartedAtMsByChainId.get(chain.chain.chainId)
                    : undefined;
                  const elapsedMs =
                    runningDurationMs === undefined
                      ? undefined
                      : Math.max(0, nowMs - runningDurationMs);
                  const lastRefreshDurationMs =
                    refreshDurationsMs[chain.chain.chainId] ?? chain.snapshot?.computeDurationMs;
                  return (
                    <tr key={chain.chain.chainId}>
                      <td style={bodyCellStyle}>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                          {chain.chain.chainName}
                        </div>
                        <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                          chain {chain.chain.chainId}
                        </div>
                      </td>
                      <td style={bodyCellStyle}>
                        {isRefreshing ? (
                          <div>
                            <div style={{ color: 'var(--accent)', fontWeight: 850 }}>
                              {elapsedMs === undefined
                                ? 'Refreshing...'
                                : `Refreshing ${formatDuration(elapsedMs)}`}
                            </div>
                            {chain.snapshot ? (
                              <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                                {formatTimestamp(chain.snapshot.computedAtUtc)}
                              </div>
                            ) : null}
                          </div>
                        ) : chain.snapshot ? (
                          <>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                flexWrap: 'wrap',
                              }}
                            >
                              <span
                                style={{
                                  color: snapshotAgeColor(chain.snapshot.ageSeconds),
                                  fontWeight: 800,
                                }}
                              >
                                {formatRelativeAge(chain.snapshot.ageSeconds)}
                                {chain.status === 'stale' ? ' · stale' : ''}
                              </span>
                              {lastRefreshDurationMs !== undefined &&
                              lastRefreshDurationMs !== null ? (
                                <span
                                  style={{
                                    color: 'var(--text-tertiary)',
                                    fontSize: 11,
                                    fontWeight: 800,
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {formatDuration(lastRefreshDurationMs)}
                                </span>
                              ) : null}
                            </div>
                            <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                              {formatTimestamp(chain.snapshot.computedAtUtc)}
                            </div>
                          </>
                        ) : (
                          <div>
                            <div style={{ color: 'var(--warning)', fontWeight: 850 }}>
                              No cached snapshot
                            </div>
                            <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                              Refresh this chain to create the first snapshot.
                            </div>
                          </div>
                        )}
                      </td>
                      <td style={monoCellStyle}>
                        {formatNumber(chain.searchedInferenceCount ?? chain.sampledInferenceCount)}
                      </td>
                      <td style={monoCellStyle}>{formatNumber(chain.rows?.length ?? 0)}</td>
                      <td style={monoCellStyle}>{formatNumber(chain.flaggedDetectionCount)}</td>
                      <td style={monoCellStyle}>
                        {formatNumber(sumChainRows(chain, (row) => row.productCount))}
                      </td>
                      <td style={monoCellStyle}>
                        {formatNumber(sumChainRows(chain, (row) => row.cartonCount))}
                      </td>
                      <td style={actionCellStyle}>
                        <button
                          type="button"
                          style={{
                            ...refreshButtonStyle,
                            opacity: isRefreshing ? 0.65 : 1,
                            cursor: isRefreshing ? 'not-allowed' : 'pointer',
                          }}
                          disabled={isRefreshing}
                          onClick={() => void handleRefreshChain(chain.chain.chainId)}
                        >
                          <RefreshOutlinedIcon style={{ fontSize: 15 }} />
                          {isRefreshing && elapsedMs !== undefined
                            ? `Refreshing ${formatDuration(elapsedMs)}`
                            : 'Refresh'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="forge-card rdc-dashboard-card">
        <div className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title">
          <div className="rdc-dashboard-section-heading">
            <TravelExploreOutlinedIcon style={{ fontSize: 18, color: 'var(--accent)' }} />
            <span>Camera image rows</span>
            <span className="rdc-dashboard-count-pill">{formatNumber(topRows.length)} rows</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              aria-label="Remote data collection chain filter"
              value={topRowsChainFilter}
              onChange={(event) => {
                const value = event.target.value;
                updateFilters({ chain: value === 'all' ? 'all' : Number(value) });
              }}
              style={{ ...controlStyle, minWidth: 190 }}
            >
              <option value="all">All supported chains</option>
              {chainOptions.map((chain) => (
                <option key={chain.chainId} value={chain.chainId}>
                  {chain.label}
                </option>
              ))}
            </select>
            <input
              aria-label="Remote data collection task prefix filter"
              value={taskPrefixFilter}
              maxLength={8}
              onChange={(event) => updateFilters({ task: event.target.value.toUpperCase() })}
              placeholder="Task prefix"
              style={{ ...controlStyle, width: 126 }}
            />
            <button
              type="button"
              style={{
                ...refreshButtonStyle,
                width: 112,
                opacity: isExportDisabled ? 0.65 : 1,
                cursor: isExportDisabled ? 'not-allowed' : 'pointer',
              }}
              disabled={isExportDisabled}
              onClick={() => void handleExportRows()}
            >
              <FileDownloadOutlinedIcon style={{ fontSize: 15 }} />
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Chain</th>
                <th style={headerCellStyle}>Camera image</th>
                <th style={headerCellStyle}>Prefix</th>
                <th style={headerCellStyle}>Priority</th>
                <th style={headerCellStyle}>Flagged</th>
                <th style={headerCellStyle}>Low emb</th>
                <th style={headerCellStyle}>Product / carton</th>
                <th style={headerCellStyle}>Boxes</th>
                <th style={headerCellStyle}>Worst sim</th>
                <th style={headerCellStyle}>Min delta</th>
                <th style={headerCellStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {topRows.length === 0 ? (
                <tr>
                  <td colSpan={11} style={bodyCellStyle}>
                    {isLoading
                      ? 'Loading rows...'
                      : loadError
                        ? 'Remote Data Collection snapshots are unavailable. Use Retry above.'
                        : 'No rows in cached snapshots.'}
                  </td>
                </tr>
              ) : (
                topRows.map((row) => {
                  const detailUrl = remoteDataCollectionToolUrl(row);
                  const cameraText = cameraRowText(row);
                  return (
                    <tr key={`${row.chainId}:${row.rowKind}:${row.rowKey}`}>
                      <td style={bodyCellStyle}>{row.chainName}</td>
                      <td style={monoCellStyle}>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                          {cameraText.title}
                        </div>
                        {cameraText.detail ? (
                          <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                            {cameraText.detail}
                          </div>
                        ) : null}
                      </td>
                      <td style={monoCellStyle}>{formatTaskPrefixes(row)}</td>
                      <td style={monoCellStyle}>{formatPriorityScore(row.priorityScore)}</td>
                      <td style={monoCellStyle}>{formatNumber(row.flaggedCount)}</td>
                      <td style={monoCellStyle}>{formatNumber(row.lowEmbeddingCount ?? 0)}</td>
                      <td style={monoCellStyle}>
                        {formatNumber(row.productCount)} / {formatNumber(row.cartonCount)}
                      </td>
                      <td style={monoCellStyle}>{formatNumber(row.detectedPolygonCount ?? 0)}</td>
                      <td style={monoCellStyle}>{formatPercent(row.worstSimilarity)}</td>
                      <td style={monoCellStyle}>{formatPercent(minDelta(row))}</td>
                      <td style={bodyCellStyle}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          {detailUrl ? (
                            <Link to={detailUrl} style={linkButtonStyle}>
                              Open Remote DC
                            </Link>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
